/**
 * Personal-world terrain (terrain realism wave) — every non-Commons world now
 * renders its OWN `terrainSeed` through the engine's erosion bake instead of
 * the legacy Commons-seed plane:
 *
 *   1. base heights from the shared analytic `terrainHeight` (the city pin
 *      still flattens the center, so the build area stays gentle),
 *   2. droplet hydraulic erosion (enginex — Beyer particle model: gullies,
 *      dendritic wash) and thermal talus relaxation (scree settling).
 *
 * The bake is deterministic per seed (seeded droplet RNG, pure math) and
 * bounded (129² grid over the 512 m plane ⇒ a few ms once per world), and it
 * is memoized. `personalGroundY` reproduces the rendered mesh's triangle
 * interpolation EXACTLY (same split as `meshHeight`), so the avatar clamp and
 * prop seating agree with the visible eroded ground.
 */
import { applySculpt } from './terrain_edits.js';
import { terrainHeight } from '@shared/terrain.js';
import { erodeDroplets, thermalErode } from 'enginex/terrain/TerrainErosion.js';

/** Footprint of the personal-world ground plane (m) — the legacy 512 m plate. */
export const PERSONAL_TERRAIN_SIZE = 512;
/** Grid segments per side (finer than the legacy 64 so erosion detail reads). */
export const PERSONAL_TERRAIN_SEG = 128;

const N = PERSONAL_TERRAIN_SEG + 1;
const CELL = PERSONAL_TERRAIN_SIZE / PERSONAL_TERRAIN_SEG;
const HALF = PERSONAL_TERRAIN_SIZE / 2;

const gridCache = new Map<string, Float32Array>();

/** FNV-1a of the seed string → deterministic integer for the droplet RNG. */
function seedInt(seed: string): number {
  let h = 2166136261 >>> 0;
  for (let i = 0; i < seed.length; i++) h = Math.imul(h ^ seed.charCodeAt(i), 16777619) >>> 0;
  return h >>> 0;
}

/** The eroded heightfield for a world seed (row = z, col = x; memoized). */
export function personalTerrainGrid(seed: string): Float32Array {
  let grid = gridCache.get(seed);
  if (grid) return grid;
  grid = new Float32Array(N * N);
  for (let row = 0; row < N; row++) {
    const z = (row / (N - 1) - 0.5) * PERSONAL_TERRAIN_SIZE;
    for (let col = 0; col < N; col++) {
      const x = (col / (N - 1) - 0.5) * PERSONAL_TERRAIN_SIZE;
      grid[row * N + col] = terrainHeight(seed, x, z);
    }
  }
  // Engine erosion: hydraulic gullies + talus settling. Deterministic per
  // seed; range-normalized internally so the gentle pin center stays gentle.
  erodeDroplets(grid, N, { droplets: N * 30, seed: seedInt(seed), radius: 2 });
  thermalErode(grid, N, { iterations: 16, cellSize: CELL });
  gridCache.set(seed, grid);
  return grid;
}

/**
 * Rendered-ground height at local/world (x, z) of a personal world — the
 * exact triangle interpolation of the mesh built from `personalTerrainGrid`
 * (same PlaneGeometry split as the Commons `meshHeight`).
 */
export function personalGroundY(seed: string, x: number, z: number): number {
  const grid = personalTerrainGrid(seed);
  let gx = (x + HALF) / CELL;
  let gz = (z + HALF) / CELL;
  if (gx < 0) gx = 0;
  if (gz < 0) gz = 0;
  if (gx > PERSONAL_TERRAIN_SEG) gx = PERSONAL_TERRAIN_SEG;
  if (gz > PERSONAL_TERRAIN_SEG) gz = PERSONAL_TERRAIN_SEG;
  let ix = Math.floor(gx);
  if (ix >= PERSONAL_TERRAIN_SEG) ix = PERSONAL_TERRAIN_SEG - 1;
  let iz = Math.floor(gz);
  if (iz >= PERSONAL_TERRAIN_SEG) iz = PERSONAL_TERRAIN_SEG - 1;
  const u = gx - ix;
  const v = gz - iz;
  const hA = grid[iz * N + ix]; // (ix,   iz)
  const hB = grid[(iz + 1) * N + ix]; // (ix,   iz+1)
  const hC = grid[(iz + 1) * N + ix + 1]; // (ix+1, iz+1)
  const hD = grid[iz * N + ix + 1]; // (ix+1, iz)
  // PlaneGeometry faces: (a,b,d) covers u+v ≤ 1, (b,c,d) covers u+v ≥ 1.
  const base = u + v <= 1
    ? hA + (hD - hA) * u + (hB - hA) * v
    : hC + (hB - hC) * (1 - u) + (hD - hC) * (1 - v);
  // Edit Mode sculpt strokes: the mesh is built FROM this function, so the
  // rendered ground and every sampler agree by construction.
  return applySculpt(base, x, z);
}
